/** Diese Woche / Nächste Woche — denní grouping z prototype/screens.jsx ThisWeek. */

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Ic } from "@/components/ui/icons";
import { WeekRow } from "@/components/ui/week-row";
import { enrichOrdersWithEndClient, fetchOrders, isoWeekRange, toDateOnly } from "@/lib/fetchers";
import { fmtWeekRange } from "@/lib/format";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";
import type { PartnerOrder } from "@/lib/types";

interface DayBucket {
  letter: string;
  short: string;
  date: Date;
  items: PartnerOrder[];
}

const DAY_LETTERS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function bucketByDay(start: Date, orders: PartnerOrder[]): DayBucket[] {
  const buckets: DayBucket[] = DAY_LETTERS.map((letter, i) => {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    return {
      letter,
      short: `${date.getDate()}.${date.getMonth() + 1}.`,
      date,
      items: [],
    };
  });

  orders.forEach((o) => {
    if (!o.scheduled_date) return;
    const d = new Date(o.scheduled_date);
    const day = d.getDay() || 7; // Mo=1..So=7
    const bucket = buckets[day - 1];
    if (bucket) bucket.items.push(o);
  });

  // Skip weekend buckets if entirely empty (Brief layout uses Mo-Fr by default,
  // but show Sa/So pokud něco padlo na ně)
  return buckets.filter((b, i) => i < 5 || b.items.length > 0);
}

interface WeekRouteProps {
  offsetWeeks: 0 | 1;
}

export function WeekRoute({ offsetWeeks }: WeekRouteProps) {
  const navigate = useNavigate();
  const { start, end } = isoWeekRange(offsetWeeks);

  const ordersQ = useQuery({
    queryKey: queryKeys.ordersWeek(toDateOnly(start)),
    queryFn: async () => {
      const list = await fetchOrders({
        fromDate: toDateOnly(start),
        toDate: toDateOnly(end),
        orderBy: { column: "scheduled_date", ascending: true },
      });
      return enrichOrdersWithEndClient(list);
    },
  });

  const buckets = bucketByDay(start, ordersQ.data || []);
  const weekNumber = isoWeekNumber(start);
  const heading = `${offsetWeeks === 0 ? t.nav.thisWeek : t.nav.nextWeek} · ${fmtWeekRange(start, end)}`;

  return (
    <div style={{ padding: "32px 36px", maxWidth: 1280, margin: "0 auto" }}>
      <div
        style={{
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--gold)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 4,
              fontWeight: 600,
            }}
          >
            KW {weekNumber}
          </div>
          <h1 className="display" style={{ margin: 0, fontSize: 28 }}>
            {heading}
          </h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" size="sm">
            <Ic.Filter size={12} /> {t.orderList.filterStatus}
          </Button>
          <Button variant="secondary" size="sm">
            <Ic.Filter size={12} /> {t.orderList.filterSystem}
          </Button>
          <Button variant="secondary" size="sm">
            <Ic.Filter size={12} /> {t.orderList.filterTeam}
          </Button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {buckets.map((b) => (
          <div key={b.letter} className="card" style={{ overflow: "hidden" }}>
            <div
              style={{
                padding: "12px 18px",
                display: "flex",
                alignItems: "center",
                gap: 14,
                background: "var(--bg-card-2)",
                borderBottom: b.items.length ? "1px solid var(--border-subtle)" : "none",
              }}
            >
              <span style={{ width: 32, fontSize: 13, fontWeight: 600 }}>{b.letter}</span>
              <span className="num" style={{ color: "var(--text-2)", fontSize: 13 }}>
                {b.short}
              </span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                {b.items.length} Auftrag{b.items.length !== 1 ? "en" : ""}
              </span>
            </div>
            {b.items.length === 0 ? (
              <div
                style={{
                  padding: "20px 18px",
                  color: "var(--text-4)",
                  fontSize: 12.5,
                }}
              >
                — {t.orderList.keinAuftrag}
              </div>
            ) : (
              <table className="tbl">
                <tbody>
                  {b.items.map((o) => (
                    <WeekRow
                      key={o.id}
                      item={{
                        orderNr: o.deal_number || o.id,
                        day: b.letter,
                        date: b.short,
                        time: null,
                        client: o.installation_contact_name || o.end_client_label || o.title || "—",
                        city: o.installation_city || "",
                        system: o.system_type || "—",
                        maker: o.device_label || undefined,
                        value: o.value_eur || 0,
                        status: o.status_label,
                      }}
                      onOpen={(it) => navigate(`/orders/${it.orderNr}`)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** ISO 8601 week number. */
function isoWeekNumber(d: Date): number {
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

export function ThisWeekRoute() {
  return <WeekRoute offsetWeeks={0} />;
}

export function NextWeekRoute() {
  return <WeekRoute offsetWeeks={1} />;
}
